import * as XLSX from 'xlsx';
import type { CourseSession } from '../types/crm';
import type { CourseSessionFinancialSnapshot } from '../services/courseSessionsService';
import type { SessionFinanceOverview } from './sessionFinanceOverview';

export type FinancialSummaryParticipantRow = {
  companyName: string;
  attendeeName: string;
  status: string;
  feeEuro: number;
  paidEuro: number;
  balanceEuro: number;
  paymentStatus: string;
  invoiceNumber: string;
  invoicePaid: string;
};

export type FinancialSummaryPaymentRow = {
  participant: string;
  company: string;
  status: string;
  date: string;
  method: string;
  amount: number;
  stepType: string;
  notes: string;
};

export type FinancialSummaryParticipantGroup = {
  label: string;
  rows: FinancialSummaryParticipantRow[];
};

export type FinancialSummaryData = {
  session: CourseSession;
  datesLabel: string;
  locationLabel: string;
  generatedAt: string;
  overview: SessionFinanceOverview;
  collectedProfit: number;
  profitTotal: number;
  totalOutlay: number;
  financialSnapshot: CourseSessionFinancialSnapshot | null;
  participantGroups: FinancialSummaryParticipantGroup[];
  paymentRows: FinancialSummaryPaymentRow[];
};

export function sanitizeFinancialSummaryFilePart(raw: string): string {
  return (
    raw
      .replace(/[<>:"/\\|?*]+/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .trim()
      .slice(0, 80) || 'Course'
  );
}

export function firstSessionDateIso(session: CourseSession): string {
  const dates = (session.dateBlocks || [])
    .map((b) => b.date)
    .filter((d): d is string => Boolean(d && /^\d{4}-\d{2}-\d{2}/.test(d)))
    .sort();
  if (dates.length > 0) return dates[0];
  return new Date().toISOString().slice(0, 10);
}

export function buildFinancialSummaryFilename(session: CourseSession): string {
  const datePrefix = firstSessionDateIso(session);
  const course = sanitizeFinancialSummaryFilePart(session.courseName || 'Course');
  const language = sanitizeFinancialSummaryFilePart(session.language || 'General');
  return `${datePrefix}_${course}_${language}_Financial_Summary.xlsx`;
}

function moneyCell(value: number): string {
  return value.toFixed(2);
}

function buildSummarySheet(data: FinancialSummaryData): XLSX.WorkSheet {
  const { session, datesLabel, locationLabel, generatedAt, overview, collectedProfit, profitTotal, totalOutlay, financialSnapshot } =
    data;
  const rows: (string | number)[][] = [
    ['Course Financial Summary'],
    ['Course', session.courseName],
    ['Language', session.language || ''],
    ['Dates', datesLabel],
    ['Location', locationLabel],
    ['Generated', generatedAt],
    [],
    ['Metric', 'Amount (EUR)'],
    ['Total fees', moneyCell(overview.totalFee)],
    ['Collected', moneyCell(overview.totalCollected)],
    ['Prepaid (deposit & other)', moneyCell(overview.totalPrepaid)],
    ['On-site (on day)', moneyCell(overview.totalDirectCollected)],
    ['Outstanding', moneyCell(overview.totalOutstanding)],
    ['Overpayment', moneyCell(overview.totalOverpayment)],
    ['Collected profit', moneyCell(collectedProfit)],
    ['Profit total', moneyCell(profitTotal)],
  ];

  if (financialSnapshot) {
    rows.push(
      [],
      ['Costs & payment methods'],
      ['Tutor cost', moneyCell(Number(financialSnapshot.tutorExpense || 0))],
      ['Interpreter cost', moneyCell(Number(financialSnapshot.interpreterExpense || 0))],
      ['Venue cost', moneyCell(Number(financialSnapshot.venueExpense || 0))],
      ['Cards', `${Number(financialSnapshot.numberCards || 0)} / ${moneyCell(Number(financialSnapshot.cardFee || 0))}`],
      [
        'Referral fees',
        moneyCell(
          Number(financialSnapshot.referralFee1 || 0) +
            Number(financialSnapshot.referralFee2 || 0) +
            Number(financialSnapshot.referralFee3 || 0)
        ),
      ],
      ['Other expenses', moneyCell(Number(financialSnapshot.otherExpenses || 0))],
      ['Total outlay', moneyCell(totalOutlay)],
      [],
      ['By method', 'Amount (EUR)', 'Count'],
      ['Stripe', moneyCell(overview.methodTotals.stripe), overview.methodCounts.stripe],
      ['Revolut', moneyCell(overview.methodTotals.revolut), overview.methodCounts.revolut],
      ['SumUp', moneyCell(overview.methodTotals.sumup), overview.methodCounts.sumup],
      ['PayPal', moneyCell(overview.methodTotals.paypal), overview.methodCounts.paypal],
      ['Bank', moneyCell(overview.methodTotals.bank), overview.methodCounts.bank],
      ['Cash', moneyCell(overview.methodTotals.cash), overview.methodCounts.cash]
    );
  }

  return XLSX.utils.aoa_to_sheet(rows);
}

function buildParticipantsSheet(data: FinancialSummaryData): XLSX.WorkSheet {
  const rows: (string | number)[][] = [
    [
      'Group',
      'Company',
      'Participant',
      'Status',
      'Fee (EUR)',
      'Paid (EUR)',
      'Balance (EUR)',
      'Payment',
      'Invoice #',
      'Invoice paid',
    ],
  ];

  for (const group of data.participantGroups) {
    for (const row of group.rows) {
      rows.push([
        group.label,
        row.companyName,
        row.attendeeName,
        row.status,
        moneyCell(row.feeEuro),
        moneyCell(row.paidEuro),
        moneyCell(row.balanceEuro),
        row.paymentStatus,
        row.invoiceNumber,
        row.invoicePaid,
      ]);
    }
  }

  return XLSX.utils.aoa_to_sheet(rows);
}

function buildPaymentsSheet(data: FinancialSummaryData): XLSX.WorkSheet {
  const rows: (string | number)[][] = [
    ['Participant', 'Company', 'Status', 'Date', 'Method', 'Amount (EUR)', 'Step type', 'Notes'],
  ];
  for (const row of data.paymentRows) {
    rows.push([
      row.participant,
      row.company,
      row.status,
      row.date,
      row.method,
      moneyCell(row.amount),
      row.stepType,
      row.notes,
    ]);
  }
  return XLSX.utils.aoa_to_sheet(rows);
}

export function buildFinancialSummaryWorkbook(data: FinancialSummaryData): XLSX.WorkBook {
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, buildSummarySheet(data), 'Summary');
  XLSX.utils.book_append_sheet(wb, buildParticipantsSheet(data), 'Participants');
  XLSX.utils.book_append_sheet(wb, buildPaymentsSheet(data), 'Payments');
  return wb;
}

export function downloadFinancialSummaryXlsx(data: FinancialSummaryData, filename?: string): void {
  const wb = buildFinancialSummaryWorkbook(data);
  const name = filename || buildFinancialSummaryFilename(data.session);
  XLSX.writeFile(wb, name);
}
